import { useEffect } from "react";
import { Box, Typography } from "@mui/material";
import {
  legalDocumentBodySx,
  legalDocumentCardSx,
  legalPageContainerSx,
  legalPageHeaderSx,
  legalPageMetaSx,
  legalPageShellSx,
  legalPageTitleSx,
} from "./styles";

const EnterpriseAddendum: React.FC = () => {
  useEffect(() => {
    document.title = "Enterprise Addendum - OmMuse";
  }, []);

  return (
    <Box sx={legalPageShellSx}>
      <Box sx={legalPageContainerSx}>
        <Box sx={legalPageHeaderSx}>
          <Typography sx={legalPageTitleSx}>
            OmMuse Enterprise Addendum
          </Typography>
          <Typography sx={legalPageMetaSx}>
            Last Updated: April 3, 2026
          </Typography>
        </Box>
        <Box sx={legalDocumentCardSx}>
          <Box sx={legalDocumentBodySx}>
            <p>
              This Enterprise Addendum (&ldquo;Addendum&rdquo;) supplements the
              OmMuse Terms of Use and applies to any enterprise customer,
              including record labels, managers, agencies, and organizations
              (&ldquo;Enterprise Customer&rdquo;).
            </p>
            <p>In the event of conflict, this Addendum shall control.</p>

            <ul>
              <li>
                <strong>1. Scope of Services</strong>
              </li>
            </ul>
            <p>OmMuse provides an enterprise-grade platform for:</p>
            <ol>
              <li>Music collaboration and project management</li>
              <li>Secure storage and organization of digital assets</li>
              <li>Controlled sharing and permission-based access</li>
              <li>Artist and team workflow coordination</li>
            </ol>

            <ul>
              <li>
                <strong>2. Data Ownership and Control</strong>
              </li>
            </ul>
            <ol>
              <li>
                Enterprise Customer retains full ownership of all content
                uploaded to the Platform
              </li>
              <li>
                OmMuse does not claim ownership of any Enterprise Customer
                content
              </li>
              <li>
                Enterprise Customer controls access permissions, sharing rights,
                and collaboration settings
              </li>
            </ol>

            <ul>
              <li>
                <strong>3. Content Responsibility</strong>
              </li>
            </ul>
            <p>Enterprise Customer represents and warrants that:</p>
            <ol>
              <li>All uploaded content is owned or properly licensed</li>
              <li>
                No content infringes any third-party intellectual property
                rights
              </li>
              <li>
                Enterprise Customer has authority to upload and share such
                content
              </li>
            </ol>
            <p>
              <strong>Explicit Restriction:</strong> Enterprise Customer shall
              not upload, store, or distribute:
            </p>
            <ol>
              <li>Copyrighted works without authorization</li>
              <li>
                Content owned by third parties without valid rights
              </li>
              <li>Any content that misrepresents ownership</li>
            </ol>

            <ul>
              <li>
                <strong>4. Security and Data Handling</strong>
              </li>
            </ul>
            <p>
              OmMuse implements commercially reasonable safeguards, including:
            </p>
            <ol>
              <li>Secure cloud infrastructure</li>
              <li>Access controls and authentication</li>
              <li>Encryption (in transit and where applicable at rest)</li>
            </ol>
            <p>
              OmMuse may use third-party providers to support operations.
            </p>

            <ul>
              <li>
                <strong>5. Confidentiality</strong>
              </li>
            </ul>
            <p>OmMuse agrees to:</p>
            <ol>
              <li>Treat Enterprise Customer content as confidential</li>
              <li>
                Not disclose such content except as required to operate the
                Platform or as required by law
              </li>
            </ol>
            <p>
              Enterprise Customer agrees not to disclose OmMuse proprietary
              technology or systems.
            </p>

            <ul>
              <li>
                <strong>6. Platform Use Restrictions</strong>
              </li>
            </ul>
            <p>Enterprise Customer shall not:</p>
            <ol>
              <li>Reverse engineer or replicate the Platform</li>
              <li>Use the Platform to build a competing product</li>
              <li>Extract or misuse platform data or workflows</li>
            </ol>

            <ul>
              <li>
                <strong>7. Service Availability</strong>
              </li>
            </ul>
            <p>
              OmMuse will use commercially reasonable efforts to maintain
              Platform availability but does not guarantee uninterrupted
              service.
            </p>

            <ul>
              <li>
                <strong>8. Data Portability</strong>
              </li>
            </ul>
            <p>
              Upon request and subject to technical feasibility, Enterprise
              Customers may export their data. Requests must be made prior to
              termination.
            </p>

            <ul>
              <li>
                <strong>9. Indemnification</strong>
              </li>
            </ul>
            <p>Enterprise Customer agrees to indemnify OmMuse for:</p>
            <ol>
              <li>IP infringement claims related to uploaded content</li>
              <li>Unauthorized use of third-party materials</li>
            </ol>

            <ul>
              <li>
                <strong>10. Limitation of Liability</strong>
              </li>
            </ul>
            <p>To the fullest extent permitted by law:</p>
            <ol>
              <li>
                OmMuse shall not be liable for indirect or consequential
                damages
              </li>
              <li>
                Total liability is capped as defined in the main Terms
              </li>
            </ol>

            <ul>
              <li>
                <strong>11. Termination</strong>
              </li>
            </ul>
            <p>Upon termination:</p>
            <ol>
              <li>Access may be revoked</li>
              <li>
                Data may be deleted after a reasonable retention period
              </li>
            </ol>

            <ul>
              <li>
                <strong>12. Governing Terms</strong>
              </li>
            </ul>
            <p>
              This Addendum incorporates the OmMuse Terms of Use and Privacy
              Policy. Continued use of OmMuse as an enterprise member after
              acceptance of this Addendum constitutes agreement to those
              documents as updated from time to time.
            </p>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default EnterpriseAddendum;
